import { Connection } from "sharedb/lib/client";
import {
  type ConnectionReceiveRequest,
  ConnectionState as _ConnectionState,
} from "sharedb/lib/sharedb";

import { PubsubMessageType } from "../shared/types/pubsub";

import { handleShareDBConnectionReadPermissionError } from "./read-permission-errors";
import { clearSubmissionTrackers, updateSubmissionTrackersAfterSend } from "./submission-tracker";

import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";
import { getWindowId } from "utils/window";

/**
 * The connection state of the ShareDB connection.
 */
export type ConnectionState = _ConnectionState;

export interface ShareDBConnectionConfig {
  sectionId: string | null;
  userId: string;
  onStateChange: (newState: ConnectionState, reason: string) => void;
  onError: (errorCode: "401" | null) => void;
}

interface ShareDbMessage extends ConnectionReceiveRequest {
  a: string;
  seq: number;
  src: string;
}

/**
 * Sets up ShareDB connection events
 */
export const setupConnectionListeners = (
  connection: Connection,
  config: ShareDBConnectionConfig,
) => {
  const { sectionId, userId, onStateChange, onError } = config;

  // Track connection state for silent failure detection
  let connectionStateChangeCount = 0;

  connection.on("connected", () => {
    connectionStateChangeCount = 0; // Reset on successful connection

    onError(null);
  });

  // Prevent uncaught EventEmitter errors when a fetch/subscribe denial has no
  // remaining doc listeners. Classify when possible; warn if we lack doc context.
  connection.on("error", (err) => {
    if (handleShareDBConnectionReadPermissionError(err, { sectionId, userId })) {
      return;
    }
    rollbarAndLogError("[ShareDBConnection] ShareDB connection error", {
      err: err?.message,
      sectionId,
      userId,
    });
  });

  connection.on("state", (newState: ConnectionState, reason: unknown) => {
    const reasonStr = stringifyReason(reason);

    // Track state changes for silent failure detection
    connectionStateChangeCount += 1;

    logInfoWithDebugFlag(
      "debug_sharedb",
      `[ShareDBConnection] ShareDB connection state changed to ${newState},`,
      `reason=${reasonStr}`,
      connectionStateChangeCount,
    );

    onStateChange(newState, reasonStr);
  });

  // Monitor connection's send events for silent failure detection
  connection.on("send", (message: ShareDbMessage) => {
    logInfoWithDebugFlag("debug_sharedb_all_messages", "Message sent", {
      sectionId,
      userId,
      windowId: getWindowId(),
      connectionId: connection.id,
      messageType: message?.a,
      message: JSON.stringify(message).substring(0, 50), // Log first 50 characters
    });

    // Update submission trackers for sent stage
    if (message?.a === "op" && message.seq) {
      // We don't have the doc here so we don't know the operation id, but we should be able
      // to match by seq if not src yet
      updateSubmissionTrackersAfterSend(message.seq, message.src, connection.id);
    }
  });

  // Monitor connection's receive events for silent failure detection
  connection.on("receive", (message: { data: ShareDbMessage }) => {
    logInfoWithDebugFlag("debug_sharedb_all_messages", "Message received", {
      sectionId,
      userId,
      windowId: getWindowId(),
      connectionId: connection.id,
      messageType:
        message?.data?.a ||
        (message?.data as unknown as { messages: PubsubMessageType[] })?.messages
          ?.map?.((msg) => msg.type)
          ?.join(", "),
      message: JSON.stringify(message).substring(0, 50), // Log first 50 characters
    });

    // Update submission trackers for received stage
    if (message?.data?.a === "op" && message?.data?.seq) {
      clearSubmissionTrackers(message.data.seq, message.data.src);
    }
  });
};

function stringifyReason(reasonObj: unknown): string {
  if (reasonObj === undefined || reasonObj === null || typeof reasonObj === "string") {
    return String(reasonObj);
  }

  try {
    if ("reason" in (reasonObj as CloseEvent)) {
      const { reason, code, wasClean } = reasonObj as CloseEvent;
      return JSON.stringify({ reason, code, wasClean });
    }
    return JSON.stringify(reasonObj);
  } catch (error) {
    logInfoWithDebugFlag("debug_sharedb", "Stringifying reasonObj failed", error);
    return `[object ${reasonObj.constructor?.name || "Object"}]`;
  }
}
